import { useState } from "react";
import { Button } from "@/components/ui/button";
import { LogIn, UserPlus, Filter, Star } from "lucide-react";
import { LINKS, GAME_GALLERY, GAME_CATEGORIES } from "@/data/site";
import { useTranslation } from "@/i18n/LanguageContext";

// Map gallery images to categories (rough mapping)
const GALLERY_TAGS = [
  ["slots"],         // image 0
  ["slots"],         // image 1
  ["slots"],         // image 2
  ["live"],          // image 3
  ["live", "sports"],// image 4
];

export default function Game() {
  const [tab, setTab] = useState("all");
  const { t } = useTranslation();

  const TABS = [
    { id: "all", label: t.gamePage.tabs.all },
    { id: "slots", label: t.gamePage.tabs.slots },
    { id: "live", label: t.gamePage.tabs.live },
    { id: "sports", label: t.gamePage.tabs.sports },
    { id: "fishing", label: t.gamePage.tabs.fishing },
  ];

  const visible = GAME_GALLERY.filter((_, idx) =>
    tab === "all" ? true : GALLERY_TAGS[idx].includes(tab)
  );

  return (
    <div data-testid="game-page" className="relative">
      {/* Banner */}
      <section className="relative overflow-hidden border-b border-orange-500/20">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-orange-500/20 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 ring-1 ring-orange-500/30 text-orange-200 text-[10px] tracking-[0.3em] uppercase">
            <Star className="w-3 h-3" /> {t.gamePage.tag}
          </div>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl tracking-wider">
            <span className="text-zinc-50">{t.gamePage.titleA}</span>
            <span className="text-orange-grad text-glow">{t.gamePage.titleB}</span>
          </h1>
          <p className="mt-4 max-w-2xl text-zinc-400">
            {t.gamePage.desc}
          </p>
        </div>
      </section>

      {/* Category quick cards */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {GAME_CATEGORIES.map((c) => {
            const tc = t.categories[c.id] || { name: c.name, desc: c.desc };
            return (
              <button
                key={c.id}
                onClick={() => setTab(c.id)}
                data-testid={`game-cat-${c.id}`}
                className={`text-left p-4 rounded-xl border transition ${
                  tab === c.id
                    ? "border-orange-500/60 bg-orange-500/10"
                    : "border-orange-500/15 bg-[#0b0b0f] hover:border-orange-500/40"
                }`}
              >
                <div className="font-display text-2xl tracking-wider text-orange-grad">
                  {tc.name}
                </div>
                <div className="text-xs text-zinc-400 mt-1 line-clamp-1">{tc.desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-10">
        <div className="flex items-center gap-2 mb-4 text-orange-200/80 text-xs tracking-[0.25em] uppercase">
          <Filter className="w-3.5 h-3.5" /> {t.common.filter}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="game-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-5 py-2 rounded-full text-sm font-medium transition ${
                tab === t.id
                  ? "bg-gradient-to-b from-orange-400 to-orange-600 text-black"
                  : "bg-white/[0.03] text-zinc-300 ring-1 ring-orange-500/15 hover:ring-orange-500/40"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery — full collages */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-10 space-y-6 pb-20">
        {visible.map((src, i) => (
          <div
            key={src}
            data-testid={`game-collage-${i}`}
            className="group relative rounded-2xl overflow-hidden border border-orange-500/20 bg-[#0c0c10]"
          >
            <img
              src={src}
              alt={`U2 Gaming providers ${i + 1}`}
              className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.01]"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#07070a]/95 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 flex flex-wrap items-center justify-between gap-3">
              <div className="text-orange-200 font-display tracking-wider text-2xl">
                {t.gamePage.collageCaption}
              </div>
              <div className="flex gap-2">
                <Button
                  asChild
                  data-testid={`collage-login-${i}`}
                  variant="outline"
                  className="rounded-full border-orange-500/40 bg-black/40 text-orange-200 hover:bg-orange-500/10"
                >
                  <a href={LINKS.login} target="_blank" rel="noreferrer">
                    <LogIn className="w-4 h-4 mr-2" /> {t.nav.login}
                  </a>
                </Button>
                <Button
                  asChild
                  data-testid={`collage-register-${i}`}
                  className="rounded-full bg-gradient-to-b from-orange-400 to-orange-600 text-black font-semibold"
                >
                  <a href={LINKS.register} target="_blank" rel="noreferrer">
                    <UserPlus className="w-4 h-4 mr-2" /> {t.nav.register}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        ))}

        {visible.length === 0 && (
          <div className="py-16 text-center text-zinc-500">
            No games for this filter — try another category.
          </div>
        )}
      </section>
    </div>
  );
}
